/**
 * ProseMirror-only: Shift+Enter inserts hard breaks in table cells/headers, and normalizes
 * `\r\n`, `\n`, or literal `\`+`n` in cell text to hard breaks. Milkdown: `tableHardBreakPlugin.ts`.
 */
import { Fragment } from '@milkdown/kit/prose/model';
import type { Mark, Node as PMNode } from '@milkdown/kit/prose/model';
import type { EditorState } from '@milkdown/kit/prose/state';

export function hardBreakType(schema: EditorState['schema']) {
  return schema.nodes.hardbreak ?? schema.nodes.hard_break;
}

export function isInTableCellPos($pos: EditorState['selection']['$from']): boolean {
  for (let d = $pos.depth; d > 0; d--) {
    const n = $pos.node(d).type.name;
    if (n === 'table_cell' || n === 'table_header') return true;
  }

  return false;
}

export function cellTextToBreakFragment(schema: EditorState['schema'], text: string, marks: readonly Mark[]): Fragment | null {
  const hardBreak = hardBreakType(schema);
  if (!hardBreak) {
    return null;
  }

  const parts: PMNode[] = [];
  let buffer = '';
  const flush = () => {
    if (buffer) parts.push(schema.text(buffer, marks));
    buffer = '';
  };

  let i = 0;
  while (i < text.length) {
    // Windows line endings
    if (text[i] === '\r' && text[i + 1] === '\n') {
      flush();
      parts.push(hardBreak.create());
      i += 2;
      continue;
    }
    // Unix line endings
    if (text[i] === '\n') {
      flush();
      parts.push(hardBreak.create());
      i += 1;
      continue;
    }
    // Literal backslash-n
    if (text[i] === '\\' && text[i + 1] === 'n') {
      flush();
      parts.push(hardBreak.create());
      i += 2;
      continue;
    }
    buffer += text[i];
    i += 1;
  }
  flush();

  const hasBreak = parts.some((p) => p.type === hardBreak);
  if (!hasBreak) return null;

  return Fragment.fromArray(parts);
}
